import * as React from 'react';
import { DataGrid, GridApi, GridColDef, GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid'; // Use GridValueFormatter
import { Button } from '@mui/material';
import DataGridViewport from './DataGridViewport';

type HeaderMapper = {
  [key: string]: { Header: string, Editable?: boolean | false, IsNumber?: boolean, DecimalPlaces?: number }
};

type NestedObject = {
  [key: string]: any;
};

const DecimalEditCell = (params: GridRenderEditCellParams & { decimalPlaces: number }) => {
  const apiRef = useGridApiContext();
  const [draft, setDraft] = React.useState(String(params.value ?? ""));

  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      autoFocus
      onChange={(event) => {
        const rawValue = event.target.value;
        if (!/^\d*(?:[.,]\d*)?$/.test(rawValue)) return;
        const separatorIndex = Math.max(rawValue.lastIndexOf("."), rawValue.lastIndexOf(","));
        const decimalDigits = separatorIndex >= 0 ? rawValue.length - separatorIndex - 1 : 0;
        if (params.decimalPlaces === 0 && separatorIndex >= 0) return;
        if (decimalDigits > params.decimalPlaces) return;
        setDraft(rawValue);
        const value = rawValue === "" ? null : Number(rawValue.replace(",", "."));
        if (rawValue === "" || Number.isFinite(value)) {
          void apiRef.current.setEditCellValue({ id: params.id, field: params.field, value }, event);
        }
      }}
      style={{ width: "100%", height: "100%", border: 0, padding: "0 8px", textAlign: "right", font: "inherit" }}
    />
  );
};

function flattenObject(obj: any, prefix = "", result: any = {}) {
  for (const key in obj) {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      // Avoid circular references
      if (obj[key] === obj) continue;
      flattenObject(obj[key], `${prefix}${key}.`, result);
    } else {
      result[`${prefix}${key}`] = obj[key];
    }
  }
  return result;
}

const getColumnsFromObject = (
  data: NestedObject[],
  headerMap: HeaderMapper,
  removeIfNotCustom: boolean,
  actionColumn?: {
    actionColumnName?: string;
    withActionColumn?: boolean;
    eventHandler?: any;
  },
  customRenderers?: {
    [key: string]: (row: any, rowIndex: number) => JSX.Element | string;
  }
): GridColDef[] => {
  const sampleData = data[0] ? flattenObject(data[0]) : {};
  const gridColDef: GridColDef[] = Object.keys(sampleData)
    .filter((key) => {
      return removeIfNotCustom ? headerMap[key.toUpperCase()]?.Header : key;
    })
    .map((key) => ({
      field: key,
      headerName: headerMap[key.toUpperCase()]?.Header || key.replace('.', ' '),
      type: headerMap[key.toUpperCase()]?.IsNumber ? "number" : undefined,
      align: headerMap[key.toUpperCase()]?.IsNumber ? "right" : "left",
      minWidth: 150,
      flex: 1,
      editable: headerMap[key.toUpperCase()]?.Editable ?? false,
      renderEditCell: headerMap[key.toUpperCase()]?.Editable && headerMap[key.toUpperCase()]?.IsNumber
        ? (params) => <DecimalEditCell {...params} decimalPlaces={headerMap[key.toUpperCase()]?.DecimalPlaces ?? 2} />
        : undefined,
      renderCell: customRenderers && customRenderers[key]
        ? (params) => customRenderers[key](params.row, params.id as number) // Apply custom renderer
        : undefined,
    }));

  if (actionColumn?.withActionColumn) {
    const actionColDef: GridColDef = {
      field: "action",
      headerName: "Action",
      sortable: false,
      renderCell: (params) => {
        const onClick = (e: any) => {
          const api: GridApi = params.api;
          const thisRow: Record<string, any> = {};

          api
            .getAllColumns()
            .filter((c) => c.field !== "__check__" && !!c)
            .forEach(
              (c) => (thisRow[c.field] = params.value(params.id, c.field))
            );
          const _row = JSON.stringify(thisRow);
          if (actionColumn.eventHandler) {
            return actionColumn.eventHandler(_row);
          } else {
            return alert(_row);
          }
        };

        return (
          <Button onClick={onClick}>
            {actionColumn?.actionColumnName ?? "Click"}
          </Button>
        );
      },
    };
    gridColDef.push(actionColDef);
  }
  return gridColDef;
};

type Props = {
  data: NestedObject[];
  customHeaderMap: HeaderMapper;
  removeIfNotCustom: boolean;
  pageSize: number;
  actionColumn?: {
    actionColumnName?: string;
    withActionColumn?: boolean;
    eventHandler?: any;
  };
  rowClickEventHandler?: any;
  customRenderers?: {
    [key: string]: (row: any, rowIndex: number) => JSX.Element;
  };
  processRowUpdate?: (newRow: NestedObject, oldRow: NestedObject) => NestedObject;
};

const DynamicGrid: React.FC<Props> = ({
  data,
  customHeaderMap,
  removeIfNotCustom,
  pageSize: initialPageSize,
  actionColumn,
  rowClickEventHandler,
  customRenderers,
  processRowUpdate,
}) => {
  const [page, setPage] = React.useState(0); // State to manage the current page
  const [pageSize, setPageSize] = React.useState(initialPageSize); // State to manage the current page size

  const rows = data.map((item, index) => ({
    id: index,
    ...flattenObject(item),
  }));
  const columns: GridColDef[] = getColumnsFromObject(
    data,
    customHeaderMap,
    removeIfNotCustom,
    actionColumn,
    customRenderers // Pass customRenderers to getColumnsFromObject
  );

  const headerHeight = 56; // Typical header height in pixels
  const rowHeight = 52; // Typical row height in pixels
  const footerHeight = 56; // Typical footer height in pixels
  const gridHeight =
    rows.length > 0
      ? Math.min(rows.length, pageSize) * rowHeight + headerHeight + footerHeight
      : footerHeight;

  return (
    <DataGridViewport height={gridHeight} minWidth={900}>
      <DataGrid
        rows={rows}
        columns={columns}
        autoPageSize={false}
        pageSizeOptions={[5, 10, 20]}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          setPage(model.page); // Update page state
          setPageSize(model.pageSize); // Update pageSize state
        }}
        sortingMode="client"
        filterMode="client"
        onRowClick={rowClickEventHandler}
        processRowUpdate={processRowUpdate}
      />
    </DataGridViewport>
  );
};

export default DynamicGrid;
